import { useEffect, useRef, useState } from 'react';
import AnimeCard from '../components/AnimeCard';

import { genreLabel } from '../constants/genreLabels';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

// Genres to hide from directory (redundant with themes/demographics or too sparse)
const GENRE_BLACKLIST = new Set(['School', 'Military', 'Magic', 'Historical', 'Super Power', 'Demons', 'Shounen']);

const STATUSES = [
  { label: 'Todos los estados', value: '' },
  { label: 'En emisión',        value: 'airing' },
  { label: 'Finalizado',        value: 'finished' },
];

const ORDERS = [
  { label: 'A-Z',          value: 'title' },
  { label: 'Z-A',          value: 'title-desc' },
  { label: 'Más reciente', value: 'date' },
  { label: 'Mejor puntuación', value: 'rating' },
];

const currentYear = new Date().getFullYear();
const YEARS = [
  { label: 'Todos los años', value: '' },
  ...Array.from({ length: currentYear - 1959 }, (_, i) => {
    const y = currentYear - i;
    return { label: String(y), value: String(y) };
  }),
];

// ── Collapsible filter section ─────────────────────────────────────────────

function FilterSection({ title, items, active, onToggle, defaultOpen = false, labelFn = (x) => x }) {
  const [open, setOpen] = useState(defaultOpen);

  if (items.length === 0) return null;

  return (
    <div className="filter-section">
      <div className="filter-section-header" onClick={() => setOpen((o) => !o)}>
        <span>{title}</span>
        <span className={`chevron${open ? ' open' : ''}`}>▼</span>
      </div>
      {open && (
        <div className="filter-checkboxes">
          {items.map((item) => (
            <label
              key={item}
              className={`filter-checkbox-item${active.includes(item) ? ' active' : ''}`}
              onClick={() => onToggle(item)}
            >
              <span className="checkmark" />
              {labelFn(item)}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Directory ──────────────────────────────────────────────────────────────

export default function Directory() {
  // Filter options from API
  const [filterOptions, setFilterOptions] = useState({ genres: [], themes: [], demographics: [] });

  // Active selections
  const [activeGenres,       setActiveGenres]       = useState([]);
  const [activeThemes,       setActiveThemes]       = useState([]);
  const [activeDemographics, setActiveDemographics] = useState([]);

  // Other filters
  const [year,   setYear]   = useState('');
  const [status, setStatus] = useState('');
  const [sort,   setSort]   = useState('title');
  const [page,   setPage]   = useState(1);

  // Results
  const [results,    setResults]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [totalPages, setTotalPages] = useState(0);

  const jumpRef = useRef(null);

  // Load filter options from backend once
  useEffect(() => {
    fetch(`${API_URL}/anime/meta/filters`)
      .then((r) => r.json())
      .then((data) => setFilterOptions(data))
      .catch(console.error);
  }, []);

  // Fetch results whenever filters change
  useEffect(() => {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    activeGenres.forEach((g) => params.append('genres', g));
    activeThemes.forEach((t) => params.append('themes', t));
    activeDemographics.forEach((d) => params.append('demographics', d));
    if (year)   params.set('year',   year);
    if (status) params.set('status', status);
    params.set('sort', sort);
    params.set('page', String(page));

    fetch(`${API_URL}/anime/browse?${params}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.error) throw new Error(data.error);
        setResults(data.results || []);
        setTotalPages(data.totalPages || 0);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [activeGenres, activeThemes, activeDemographics, year, status, sort, page]);

  // ── Helpers ──────────────────────────────────────────────────────────────

  const toggle = (setter) => (val) => {
    setter((prev) => prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]);
    setPage(1);
  };

  const removeFilter = (val) => {
    setActiveGenres((prev) => prev.filter((v) => v !== val));
    setActiveThemes((prev) => prev.filter((v) => v !== val));
    setActiveDemographics((prev) => prev.filter((v) => v !== val));
    setPage(1);
  };

  const clearAllFilters = () => {
    setActiveGenres([]);
    setActiveThemes([]);
    setActiveDemographics([]);
    setPage(1);
  };

  const goPage = (n) => {
    const clamped = Math.max(1, totalPages ? Math.min(n, totalPages) : n);
    setPage(clamped);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleJump = (e) => {
    e.preventDefault();
    const n = parseInt(jumpRef.current?.value, 10);
    if (n) goPage(n);
  };

  const hasActiveFilters = activeGenres.length > 0 || activeThemes.length > 0 || activeDemographics.length > 0;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="dir-page">
      <div className="dir-header">
        <h1>Directorio</h1>
        <p>Explorá el catálogo completo de anime</p>
      </div>

      {/* ── Filter panel ── */}
      <div className="directory-filters">
        <FilterSection
          title="Géneros"
          items={filterOptions.genres.filter((g) => !GENRE_BLACKLIST.has(g))}
          active={activeGenres}
          onToggle={toggle(setActiveGenres)}
          defaultOpen={true}
          labelFn={genreLabel}
        />
        <FilterSection
          title="Temas"
          items={filterOptions.themes}
          active={activeThemes}
          onToggle={toggle(setActiveThemes)}
          defaultOpen={false}
          labelFn={genreLabel}
        />
        <FilterSection
          title="Demografía"
          items={filterOptions.demographics}
          active={activeDemographics}
          onToggle={toggle(setActiveDemographics)}
          defaultOpen={false}
        />

        {hasActiveFilters && (
          <div className="active-filters">
            {[...activeGenres, ...activeThemes, ...activeDemographics].map((f) => (
              <span key={f} className="filter-chip">
                {genreLabel(f)}
                <button onClick={() => removeFilter(f)} aria-label={`Quitar ${genreLabel(f)}`}>×</button>
              </span>
            ))}
            <button className="clear-all" onClick={clearAllFilters}>Limpiar todo</button>
          </div>
        )}
      </div>

      {/* ── Secondary filters bar ── */}
      <div className="filters-bar">
        <select className="filter-select" value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }}>
          {ORDERS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select className="filter-select" value={year} onChange={(e) => { setYear(e.target.value); setPage(1); }}>
          {YEARS.map((y) => <option key={y.value} value={y.value}>{y.label}</option>)}
        </select>
        <select className="filter-select" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      {/* ── Skeleton ── */}
      {loading && (
        <div className="dir-grid">
          {Array.from({ length: 24 }).map((_, i) => (
            <div key={i} className="acard" style={{ opacity: 0.45 }}>
              <div style={{ aspectRatio: '2/3', background: 'var(--bg-elevated)' }} />
              <div className="acard__meta">
                <div style={{ height: 10, background: 'var(--bg-elevated)', borderRadius: 4, marginBottom: 6 }} />
                <div style={{ height: 8, width: '60%', background: 'var(--bg-elevated)', borderRadius: 4 }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {error && <div className="state-center state-error">{error}</div>}

      {!loading && !error && (
        <>
          <div className="dir-grid">
            {results.map((anime, index) => (
              <AnimeCard key={`${anime.id}-${index}`} anime={anime} />
            ))}
            {results.length === 0 && (
              <p style={{ color: 'var(--text-sec)', gridColumn: '1/-1', padding: '3rem 0' }}>
                No se encontraron resultados con estos filtros.
              </p>
            )}
          </div>

          {totalPages > 0 && (
            <div className="pagination">
              <button className="pag-btn" onClick={() => goPage(page - 1)} disabled={page <= 1}>
                ← Anterior
              </button>
              <span className="pag-current">Página {page} de {totalPages}</span>
              <button className="pag-btn" onClick={() => goPage(page + 1)} disabled={page >= totalPages}>
                Siguiente →
              </button>
              {totalPages > 10 && (
                <form className="pag-jump" onSubmit={handleJump}>
                  <label className="pag-jump__label">Ir a:</label>
                  <input
                    ref={jumpRef}
                    className="pag-jump__input"
                    type="number"
                    min={1}
                    max={totalPages}
                    placeholder="Pág."
                  />
                  <button type="submit" className="pag-btn">→</button>
                </form>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
